import React from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@shadcn-in-nx/ui/dialog";
import { FileCode2, Copy } from "lucide-react";
import { toast } from "sonner";

const constraintsOf = (c) => {
    const o = [];
    if (Array.isArray(c.enum) && c.enum.length > 0) {
        const preview = c.enum.slice(0, 5).join(", ");
        o.push(`enum: ${preview}${c.enum.length > 5 ? ` (+${c.enum.length - 5})` : ""}`);
    }
    if (c.const !== undefined) o.push(`const: ${JSON.stringify(c.const)}`);
    if (c.minLength !== undefined) o.push(`min ${c.minLength}`);
    if (c.maxLength !== undefined) o.push(`max ${c.maxLength}`);
    if (c.minimum !== undefined) o.push(`≥ ${c.minimum}`);
    if (c.maximum !== undefined) o.push(`≤ ${c.maximum}`);
    if (c.exclusiveMinimum !== undefined) o.push(`> ${c.exclusiveMinimum}`);
    if (c.exclusiveMaximum !== undefined) o.push(`< ${c.exclusiveMaximum}`);
    if (c.multipleOf !== undefined) o.push(`× ${c.multipleOf}`);
    if (c.pattern) o.push(c.examples?.[0] ? `e.g. ${c.examples[0]}` : `pattern: ${c.patternError || c.pattern}`);
    if (c.format) o.push(`format: ${c.format}`);
    if (c.default !== undefined) o.push(`default: ${JSON.stringify(c.default)}`);
    if (c.nullable) o.push("nullable");
    return o;
};

const Row = ({ c }) => (
    <div className="p-2.5 text-sm">
        <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold">{c.name}</span>
            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{c.type || "string"}</span>
            {c.required && <span className="text-xs px-1.5 py-0.5 rounded bg-red-50 text-red-700">required</span>}
            {constraintsOf(c).map((x, i) => <span key={i} className="text-xs text-muted-foreground">{x}</span>)}
        </div>
        {c.description && <div className="mt-1 text-xs text-muted-foreground">{c.description}</div>}
    </div>
);

const Section = ({ title, items, muted, emptyText }) => (
    <div className="mt-2">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">{title}</div>
        {items.length === 0
            ? <div className="text-sm text-muted-foreground italic">{emptyText}</div>
            : <div className={`border rounded-md divide-y ${muted ? "opacity-80" : ""}`}>{items.map((c) => <Row key={c.name} c={c} />)}</div>}
    </div>
);

function TableSchemaDialog({ columns, tableName }) {
    const userCols = columns.filter((c) => !c.system && c.name !== "id" && c.name !== "key");
    const systemCols = columns.filter((c) => c.system);

    const copyJson = async () => {
        const keep = ["type", "description", "format", "pattern", "patternError",
            "minLength", "maxLength", "minimum", "maximum",
            "exclusiveMinimum", "exclusiveMaximum", "multipleOf",
            "enum", "const", "default", "nullable", "examples", "uiHint"];
        const json = Object.fromEntries(userCols.map((c) => {
            const out = { required: !!c.required };
            for (const k of keep) if (c[k] !== undefined) out[k] = c[k];
            return [c.name, out];
        }));
        await navigator.clipboard.writeText(JSON.stringify(json, null, 2));
        toast.success("Schema copied to clipboard");
    };

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="flex-shrink-0">
                    <FileCode2 className="w-4 h-4 md:mr-2" />
                    <span className="hidden md:inline">Schema</span>
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center justify-between gap-3">
                        <span className="truncate">{tableName ? `${tableName} schema` : "Schema"}</span>
                        <Button variant="ghost" size="sm" onClick={copyJson} className="h-7">
                            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy JSON
                        </Button>
                    </DialogTitle>
                </DialogHeader>
                <Section title="Fields" items={userCols} emptyText="No user-defined fields" />
                {systemCols.length > 0 && <Section title="System columns (read-only)" items={systemCols} muted />}
            </DialogContent>
        </Dialog>
    );
}

export default React.memo(TableSchemaDialog);
